import Button from "@jbhi-fi/lanyard-ui/components/Button/Button";
import {
  Alert,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { CalendarIcon, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Moment } from "moment-timezone";
import Papa from "papaparse";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useCountry } from "src/components/Context/CountryContext";
import PageContext from "src/components/Context/PageContext";
import { useCurrentUserStore } from "src/hooks/useCurrentUserStore";
import { useTitle } from "src/hooks/useTitle";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { FutureTicketItem } from "src/models/ticketing/FutureTicketItem";
import { FutureTicketRequest } from "src/models/ticketing/FutureTicketRequest";
import { booleanType, currencyType } from "src/utils/dataGridFormat";
import { getFutureTicketNames, getFutureTicketValues, postFutureTicket } from "src/utils/productAppApiHelper";
import DataGrid from "../Product/Desktop/components/DataGrid";
import { makeStyles } from "@mui/styles";

var moment = require("moment-timezone");
moment.tz.setDefault("Australia/Melbourne");

const FuturePricing: React.FC = () => {
  const [currentTicketName, setCurrentTicketName] = useState<string>("");
  const [startDateOpen, setStartDateOpen] = useState<boolean>(false);
  const [id, setId] = useState<undefined | string>(undefined);

  const [startDate, setStartDate] = useState<Moment | null>(moment().add("days", 1));
  const [errors, setErrors] = useState<string[]>([]);
  const [skus, setSkus] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { country, timezone } = useCountry();
  const momentInstance = moment.tz.setDefault(timezone);

  const [values, setValues] = useState<string[]>([]);

  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [futureTickets, setFutureTickets] = useState<FutureTicketItem[]>([]);

  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" as "error" | "success" });

  const showToast = useCallback((message: string, severity: "error" | "success" = "error") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false, message: "" }));
  };

  const title = "Future Pricing";
  useTitle(title);

  useEffect(() => {
    getFutureTicketNames(country).then((x) => {
      setValues(x);
    });
  }, [country]);

  const handleFetch = async () => {
    setSearching(true);
    var items = await getFutureTicketValues(currentTicketName, country);
    setFutureTickets(items);
    setSearching(false);
  };

  useMemo(() => {
    if (currentTicketName != "") {
      handleFetch();
    }
  }, [currentTicketName]);

  if (!hasPermission(UserRoles.PricingHistoricPricing, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }

  const handleBulkUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    setId(undefined);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        complete: (results) => {
          const newSkus = results.data
            .map((row: any[]) => row[0])
            .filter((sku: any) => typeof sku === "string" && sku.trim() !== "" && sku.toLocaleLowerCase() !== "sku");

          const updatedSkus = [...new Set([...skus, ...newSkus])]; // Merge and deduplicate

          setSkus(updatedSkus);
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          showToast("Error parsing CSV file");
        },
      });
    }
    if (event.target) {
      event.target.value = "";
    }
  };

  const sendRequest = async () => {
    setSubmitting(true);
    const now = moment();
    var newRequest = {
      skus: skus,
      source: `${userProfile.givenName}${userProfile.surname}-${now.format("yyyy-MM-DD-HH-mm")}`,
      calculationDate: startDate.format("yyyy-MM-DD"),
    } as FutureTicketRequest;
    await postFutureTicket(newRequest, country);
    setSkus([]);
    setValues([newRequest.source, ...values]);
    setId(newRequest.source);
    setSubmitting(false);
    setCurrentTicketName(newRequest.source);
  };

  const columns: GridColDef[] = [
    {
      field: "sku",
      headerName: "Sku",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "price.displayPrice",
      headerName: "Display Price",
      ...currencyType,
      valueGetter: (value) => {
        return value.row.price.displayPrice;
      },
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "price.wasPrice",
      headerName: "Was Price",
      ...currencyType,
      valueGetter: (value) => {
        return value.row.price.wasPrice;
      },
      flex: 1,
      headerAlign: "left",
      align: "left",
    },

    {
      field: "price.displayWasPrice",
      headerName: "Display Was Price",
      ...booleanType,
      valueGetter: (value) => {
        return value.row.price.displayWasPrice;
      },
      width: 100,
    },
    {
      field: "price.promosApplied",
      headerName: "Promos Applied",
      width: 120,
      valueGetter: (value) => value.row.price.promosApplied,
      valueFormatter: (params) => `${params.value.join(", ")}`,
      renderCell: (value) => {
        return (
          <div>
            {value.value.map((v, index) => (
              <div key={index}>{v}</div>
            ))}
          </div>
        );
      },
    },
    {
      field: "allCalculatedTags",
      headerName: "Calculated Tags",
      valueFormatter: (value) => `${value.value.map((x) => x.value).join(", ")}`,
      renderCell: (value) => {
        return (
          <div>
            {value.value.map((v, index) => (
              <div key={index}>{v.value}</div>
            ))}
          </div>
        );
      },
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "calculatedForDate",
      headerName: "Calculated For Date",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
  ];

  const skuColumns: GridColDef[] = [
    {
      field: "sku",
      headerName: "Sku",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={momentInstance}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Grid container paddingBottom={2}>
        <Grid item xs={12} paddingBottom={2}>
          <h1>{title}</h1>
        </Grid>

        <Grid
          item
          xs={12}
          border={1}
          borderColor="lightgrey"
          paddingLeft={4}
          paddingRight={4}
          paddingBottom={4}
          marginBottom={5}
        >
          <Grid container spacing={1} justifyContent="space-between" alignItems="center" pt={2} rowGap={3}>
            <Grid item xs={12}>
              <h3>Upload Skus</h3>
            </Grid>
            <Grid xs={3}>
              <Button type={"primary"} onClick={handleBulkUploadClick} disabled={searching}>
                Bulk Upload
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".csv"
                onChange={handleFileChange}
              />
            </Grid>
            <Grid xs={6}>
              <DatePicker
                open={startDateOpen}
                onClose={() => setStartDateOpen(false)} // This is called when the picker requests to be closed
                onAccept={() => setStartDateOpen(false)}
                sx={{ width: "100%" }}
                label="Future Date"
                value={startDate}
                disabled={skus.length === 0}
                onChange={(newValue: Moment | null) => {
                  const errorMsg = "Invalid date and time format";

                  if (newValue && newValue.isValid()) {
                    setStartDate(newValue);
                    setErrors((prevErrors) => prevErrors.filter((err) => err !== errorMsg));
                  } else {
                    if (!errors.includes(errorMsg)) {
                      setErrors((prevErrors) => [...prevErrors, errorMsg]);
                    }
                  }
                }}
                minDate={moment().add("days", 1)}
                maxDate={moment().add("months", 1)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    InputProps: {
                      endAdornment: (
                        <>
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setStartDateOpen(true);
                              }}
                              edge="end"
                            >
                              <CalendarIcon />
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    },
                  },
                }}
              />
            </Grid>
            <Grid xs={2}>
              <Button
                type={"primary"}
                onClick={sendRequest}
                loading={submitting}
                disabled={skus.length == 0 || submitting}
              >
                Submit
              </Button>
            </Grid>
            <Grid xs={3}>
              <Link
                href="https://jbhuiprdv3prod.blob.core.windows.net/public/future-pricing-bulk-upload.csv"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download bulk upload template
              </Link>
            </Grid>

            <Grid xs={6}>
              <Typography style={{ color: "gray", fontSize: "14px" }}>
                It may take up to 5 minutes to process, please use the refresh button to retrieve
              </Typography>
            </Grid>
            <Grid xs={2}></Grid>
            {skus.length > 0 && (
              <>
                <Grid item xs={12} paddingBottom={2}>
                  <h2>Skus to submit</h2>
                </Grid>
                <Grid container marginTop={2} paddingBottom={5}>
                  <DataGrid
                    columns={skuColumns}
                    experimentalFeatures={{ columnGrouping: true }}
                    rows={skus.map((x) => {
                      return { sku: x };
                    })}
                    getRowId={() => crypto.randomUUID()}
                    hideFooter
                    isLoading={false}
                    autoHeight
                    disableColumnResize
                    getRowHeight={() => "auto"}
                    sx={{
                      "& .MuiDataGrid-row": {
                        minHeight: "52px !important",
                      },
                    }}
                    pagination={true}
                    pageSizeOptions={[5, 10, 25]}
                  ></DataGrid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} pb={4}>
          <h3>Review Future Pricing</h3>
        </Grid>

        <Grid item paddingRight={2} xs={4}>
          <Select
            defaultValue={currentTicketName}
            value={currentTicketName}
            onChange={(event) => {
              setCurrentTicketName(event.target.value);
            }}
            label="Upload ID"
            style={{ minWidth: "100%" }}
          >
            {values.map((value) => {
              return (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              );
            })}
          </Select>
        </Grid>
        <Grid item xs={8}>
          <Button type={"primary"} onClick={handleFetch} loading={searching} disabled={searching}>
            Refresh
          </Button>
        </Grid>
      </Grid>

      {futureTickets.length > 0 && (
        <>
          <Grid container marginTop={2}>
            <DataGrid
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: "sku", sort: "asc" }],
                },
              }}
              experimentalFeatures={{ columnGrouping: true }}
              rows={futureTickets}
              getRowId={() => crypto.randomUUID()}
              isLoading={searching}
              hideFooter
              autoHeight
              isRowSelectable={() => false}
              disableColumnResize
              rowSelection={false}
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-row": {
                  minHeight: "52px !important",
                },
              }}
            ></DataGrid>
          </Grid>
        </>
      )}
    </LocalizationProvider>
  );
};

export default FuturePricing;
